<template>
    <div class="stl-site-tab">
        <!-- ─── 시안 B: 레일 + 탭 레이아웃 ─── -->
        <div class="stl-site-layout">
            <!-- ── LEFT PROFILE RAIL (332px 고정) ── -->
            <div class="stl-site-rail">
                <div class="stl-site-rail-body">
                    <!-- 단지 아바타 + 이름 -->
                    <div class="stl-site-avatar-block">
                        <div class="stl-site-avatar">{{ siteInitial }}</div>
                        <div class="stl-site-avatar-meta">
                            <div class="stl-site-avatar-name">{{ form.siteName || "단지명 미입력" }}</div>
                            <div class="stl-site-avatar-addr">
                                {{ form.address || "주소 미입력" }}
                                <button
                                    v-if="!form.address && !editMode"
                                    type="button"
                                    class="stl-site-addr-cta"
                                    @click="editMode = true"
                                >
                                    입력
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- ── 읽기 모드 ── -->
                    <template v-if="!editMode">
                        <!-- KV 팩트 카드 -->
                        <div class="stl-site-kv-card">
                            <div class="stl-site-kv-row">
                                <span class="k">검침일</span>
                                <span class="v">매월 {{ form.readingDay || "—" }}일</span>
                            </div>
                            <div class="stl-site-kv-row">
                                <span class="k">총면적 분모</span>
                                <span class="v">{{ numFmt(form.fixedTotalAreaM2) }} ㎡</span>
                            </div>
                            <div v-if="households.length" class="stl-site-kv-row">
                                <span class="k">세대 면적 합</span>
                                <span class="v">{{ numFmt(areaSum) }} ㎡</span>
                            </div>
                            <div class="stl-site-kv-row">
                                <span class="k">등록 세대수</span>
                                <span class="v">{{ displayHouseholdCount }} 세대</span>
                            </div>
                        </div>

                        <!-- 에너지 · 연동 -->
                        <div class="stl-site-rail-section-lbl">에너지 · 연동</div>
                        <div class="stl-site-energy-pills">
                            <div class="stl-site-energy-pill" :class="elecEnabledBool ? 'on' : 'off'">
                                <span class="dot"></span>
                                <span>전기 정산</span>
                                <span class="sub">{{ elecEnabledBool ? "사용" : "미사용" }}</span>
                            </div>
                            <div class="stl-site-energy-pill" :class="kepcoEnabledBool ? 'on' : 'off'">
                                <span class="dot"></span>
                                <span>한전 연동</span>
                                <span class="sub">{{ kepcoEnabledBool ? "연동" : "수동 입력" }}</span>
                            </div>
                            <div class="stl-site-energy-pill" :class="gasEnabledBool ? 'on' : 'off'">
                                <span class="dot"></span>
                                <span>가스 정산</span>
                                <span class="sub">{{ gasEnabledBool ? "사용" : "미사용" }}</span>
                            </div>
                            <div class="stl-site-energy-pill" :class="waterEnabledBool ? 'on' : 'off'">
                                <span class="dot"></span>
                                <span>수도 정산</span>
                                <span class="sub">{{ waterEnabledBool ? "사용" : "미사용" }}</span>
                            </div>
                            <div
                                v-if="elecEnabledBool && kepcoEnabledBool && form.kepcoCustNo"
                                class="stl-site-kepco-no"
                            >
                                한전 고객번호 <strong>{{ form.kepcoCustNo }}</strong>
                            </div>
                        </div>
                    </template>

                    <!-- ── 편집 모드 ── -->
                    <template v-else>
                        <div v-if="hasFinal" class="stl-banner stl-banner-warning">
                            <i class="fa fa-exclamation-triangle"></i>
                            <span>확정된 정산이 있습니다. 변경은 이후 정산부터 반영됩니다.</span>
                        </div>
                        <div class="stl-site-edit-fields">
                            <div class="stl-field">
                                <label>단지명</label>
                                <input v-model="form.siteName" type="text" class="stl-input" />
                            </div>
                            <div class="stl-field">
                                <label>주소</label>
                                <input v-model="form.address" type="text" class="stl-input" />
                            </div>
                            <div class="stl-field">
                                <label>검침일</label>
                                <input
                                    v-model.number="form.readingDay"
                                    type="number"
                                    min="1"
                                    max="31"
                                    class="stl-input stl-num"
                                />
                            </div>
                            <div class="stl-field">
                                <label>총 면적 분모 (㎡)</label>
                                <input
                                    v-model.number="form.fixedTotalAreaM2"
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    class="stl-input stl-num"
                                />
                            </div>
                            <div class="stl-field">
                                <label>등록 세대수</label>
                                <input
                                    v-model.number="form.householdCount"
                                    type="number"
                                    min="0"
                                    class="stl-input stl-num"
                                />
                            </div>
                        </div>
                        <!-- 에너지 · 연동 편집 -->
                        <div class="stl-site-energy-edit">
                            <div class="stl-site-rail-section-lbl">에너지 · 연동</div>
                            <label class="stl-check-label">
                                <input v-model="elecEnabledBool" type="checkbox" /> 전기 정산
                            </label>
                            <label class="stl-check-label">
                                <input v-model="kepcoEnabledBool" type="checkbox" :disabled="!elecEnabledBool" /> 한전
                                연동
                            </label>
                            <input
                                v-if="kepcoEnabledBool && elecEnabledBool"
                                v-model="form.kepcoCustNo"
                                type="text"
                                placeholder="한전 고객번호"
                                class="stl-input stl-site-energy-input"
                            />
                            <label class="stl-check-label">
                                <input v-model="gasEnabledBool" type="checkbox" /> 가스 정산
                            </label>
                            <label class="stl-check-label">
                                <input v-model="waterEnabledBool" type="checkbox" /> 수도 정산
                            </label>
                            <div class="stl-field" style="margin-top: 4px">
                                <label>비고</label>
                                <input v-model="form.remarks" type="text" class="stl-input" />
                            </div>
                        </div>
                    </template>
                </div>

                <!-- 레일 푸터 버튼 -->
                <div class="stl-site-rail-footer">
                    <template v-if="!editMode">
                        <button
                            type="button"
                            class="stl-btn"
                            :class="!form.address ? 'stl-btn-primary' : 'stl-btn-tint'"
                            style="width: 100%"
                            @click="editMode = true"
                        >
                            기본정보 수정
                            <span v-if="!form.address" class="stl-site-edit-hint"> · 주소 미입력</span>
                        </button>
                    </template>
                    <template v-else>
                        <div style="display: flex; gap: 8px">
                            <button
                                type="button"
                                class="stl-btn stl-btn-primary"
                                style="flex: 1"
                                :disabled="saving"
                                @click="saveAndExit"
                            >
                                <b-spinner v-if="saving" small />
                                <span v-else>저장</span>
                            </button>
                            <button type="button" class="stl-btn" @click="cancelEdit">취소</button>
                        </div>
                    </template>
                </div>
            </div>

            <!-- ── RIGHT: 탭 콘텐츠 ── -->
            <div class="stl-site-right">
                <!-- 탭 바 -->
                <div class="stl-site-tab-bar">
                    <button type="button" :class="{ on: activeTab === 'households' }" @click="activeTab = 'households'">
                        세대 정보
                        <span class="cnt">{{ loadingHouseholds ? "…" : households.length }}</span>
                    </button>
                    <button type="button" :class="{ on: activeTab === 'scope' }" @click="activeTab = 'scope'">
                        정산 범위
                    </button>
                </div>

                <!-- 탭 콘텐츠 -->
                <div class="stl-site-tab-content">
                    <!-- 세대 마스터 탭 -->
                    <template v-if="activeTab === 'households'">
                        <div class="stl-site-tab-content-inner">
                            <!-- 툴바 -->
                            <div class="stl-site-households-toolbar">
                                <span
                                    v-if="areaMatchText"
                                    :class="areaMatch ? 'stl-site-area-ok' : 'stl-site-area-warn'"
                                >
                                    <i class="stl-tick" :class="{ idle: !areaMatch }">{{ areaMatch ? "✓" : "!" }}</i>
                                    {{ areaMatchText }}
                                </span>
                                <span class="sp"></span>
                                <button type="button" class="stl-btn stl-btn-sm" :disabled="syncing" @click="resync">
                                    <b-spinner v-if="syncing" small />
                                    재동기화
                                </button>
                                <button type="button" class="stl-btn stl-btn-sm" @click="downloadTemplate">엑셀</button>
                                <label class="stl-btn stl-btn-sm" style="margin: 0; cursor: pointer">
                                    <input type="file" accept=".xlsx,.xls,.csv" hidden @change="onImportFile" />
                                    import
                                </label>
                                <button
                                    type="button"
                                    class="stl-btn stl-btn-sm"
                                    :disabled="validating"
                                    @click="runValidate"
                                >
                                    <b-spinner v-if="validating" small />
                                    검증
                                </button>
                            </div>

                            <div v-if="syncResult" class="stl-banner stl-banner-info">
                                <i class="fa fa-info-circle"></i>
                                <span>
                                    동기화 — 신규 {{ syncResult.inserted }} / 갱신 {{ syncResult.updated }} / 건너뜀
                                    {{ syncResult.skipped }}
                                    <template v-if="(syncResult.warnings || []).length"
                                        >/ 경고 {{ syncResult.warnings.length }}건</template
                                    >
                                </span>
                            </div>

                            <!-- 세대 테이블 -->
                            <div class="stl-site-table-wrap">
                                <div v-if="loadingHouseholds" class="stl-empty stl-site-hh-loading">
                                    <b-spinner variant="primary" small />
                                    <p>세대 목록을 불러오는 중…</p>
                                </div>
                                <table v-else class="stl-table">
                                    <thead>
                                        <tr>
                                            <th>호명</th>
                                            <th class="num">전용면적(㎡)</th>
                                            <th>형별</th>
                                            <th>입주</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="h in households"
                                            :key="h.householdIdx"
                                            :class="{ 'row-warning': !h.exclusiveArea || h.exclusiveArea <= 0 }"
                                        >
                                            <td class="ho">{{ h.roomName }}</td>
                                            <td class="num">
                                                <input
                                                    v-model.number="h.exclusiveArea"
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    class="stl-input stl-num"
                                                    style="width: 90px; height: 28px"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    v-model="h.typeName"
                                                    type="text"
                                                    class="stl-input"
                                                    style="width: 90px; height: 28px"
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    v-model="h.isOccupied"
                                                    class="stl-select"
                                                    style="height: 28px; width: 76px"
                                                >
                                                    <option value="Y">입주</option>
                                                    <option value="N">공실</option>
                                                </select>
                                            </td>
                                        </tr>
                                        <tr v-if="!households.length">
                                            <td colspan="4">
                                                <div class="stl-empty">
                                                    <p>등록된 세대가 없습니다. [재동기화]를 실행하세요.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- 검증 결과 -->
                            <div v-if="validateResult" class="stl-site-validate-result">
                                <div class="stl-site-validate-title">
                                    검증 결과 — {{ validateResult.ok ? "정상" : "조치 필요" }}
                                    <i class="stl-tick" :class="{ idle: !validateResult.ok }">{{
                                        validateResult.ok ? "✓" : "!"
                                    }}</i>
                                </div>
                                <div class="stl-table-container" style="max-height: 160px">
                                    <table class="stl-table">
                                        <thead>
                                            <tr>
                                                <th style="width: 50px">결과</th>
                                                <th>항목</th>
                                                <th>상세</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="c in validateResult.checks"
                                                :key="c.code"
                                                :class="{ 'row-error': !c.ok }"
                                            >
                                                <td>
                                                    <i
                                                        class="fa"
                                                        :class="c.ok ? 'fa-check-circle' : 'fa-times-circle'"
                                                        :style="{ color: c.ok ? '#1e7e52' : '#c13a33' }"
                                                    ></i>
                                                </td>
                                                <td class="nodeid">{{ c.code }}</td>
                                                <td style="white-space: normal">{{ c.detail }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div
                                    v-if="(validateResult.warnings || []).length"
                                    class="stl-banner stl-banner-warning"
                                    style="margin-top: 8px"
                                >
                                    <i class="fa fa-exclamation-triangle"></i>
                                    <div>
                                        <div v-for="(w, i) in validateResult.warnings" :key="i">
                                            {{ typeof w === "string" ? w : w.message || JSON.stringify(w) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- 정산 계통 탭 -->
                    <template v-else>
                        <div class="stl-site-tab-content-inner">
                            <div class="stl-note" style="margin-bottom: 0">
                                단지 → 동/공용 → 세대 → 설비 구조입니다.
                            </div>
                            <div class="stl-box-scroll stl-box-scroll--tree" style="flex: 1; min-height: 120px">
                                <ScopeTreePreview :tree="scopeTree" :loading="loadingTree" />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- 하단: 세대 저장 전용 (단지 기본정보는 좌측 레일에서 저장) -->
        <div class="stl-site-footer">
            <span
                v-if="validateResult && validateResult.ok"
                class="dim"
                style="font-size: 12px; display: flex; align-items: center; gap: 7px"
            >
                <i class="stl-tick">✓</i>검증 통과 — 세대 저장 가능
            </span>
            <span v-else-if="editMode" class="dim" style="font-size: 12px"
                >기본정보 변경은 좌측 패널에서 저장하세요</span
            >
            <span class="sp"></span>
            <button
                type="button"
                class="stl-btn stl-btn-primary"
                :disabled="savingHouseholds || !households.length"
                @click="saveHouseholds"
            >
                <b-spinner v-if="savingHouseholds" small />
                세대 일괄 저장
            </button>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import ScopeTreePreview from "../components/ScopeTreePreview.vue";
import { parseApiError } from "../utils/settlementFormat.js";
import * as XLSX from "xlsx";

export default {
    name: "SettlementSiteTab",
    components: { ScopeTreePreview },
    props: {
        site: { type: Object, required: true },
    },
    data() {
        return {
            form: {
                siteName: this.site.siteName,
                address: this.site.address || "",
                readingDay: this.site.readingDay,
                fixedTotalAreaM2: parseFloat(this.site.fixedTotalAreaM2 || 0),
                householdCount: this.site.householdCount || null,
                kepcoCustNo: this.site.kepcoCustNo || "",
                remarks: this.site.remarks || "",
            },
            kepcoEnabledBool: this.site.kepcoEnabled === "Y",
            elecEnabledBool: this.site.elecEnabled !== "N",
            gasEnabledBool: this.site.gasEnabled === "Y",
            waterEnabledBool: this.site.waterEnabled === "Y",

            editMode: false,
            activeTab: "households",

            households: [],
            loadingHouseholds: true,
            equipSummary: null,
            scopeTree: null,
            loadingTree: false,

            hasFinal: false,
            saving: false,
            savingHouseholds: false,
            syncing: false,
            validating: false,
            syncResult: null,
            validateResult: null,
        };
    },
    computed: {
        siteInitial() {
            const name = this.form.siteName || "";
            const match = name.match(/(\d+)/);
            if (match) return match[1];
            return name.charAt(0) || "?";
        },
        areaSum() {
            return this.households.reduce((s, h) => s + Number(h.exclusiveArea || 0), 0);
        },
        areaMatch() {
            return (
                this.households.length > 0 && Math.abs(this.areaSum - Number(this.form.fixedTotalAreaM2 || 0)) < 0.0001
            );
        },
        areaMatchText() {
            if (!this.households.length) return null;
            const sum = this.areaSum.toLocaleString("ko-KR", { maximumFractionDigits: 2 });
            return this.areaMatch ? `전용면적 합 ${sum}㎡ = 분모 일치` : `전용면적 합 ${sum}㎡`;
        },
        displayHouseholdCount() {
            if (this.loadingHouseholds) {
                if (this.form.householdCount != null && this.form.householdCount !== "") return this.form.householdCount;
                return "…";
            }
            if (this.households.length) return this.households.length;
            if (this.form.householdCount != null && this.form.householdCount !== "") return this.form.householdCount;
            return "—";
        },
    },
    watch: {
        site: {
            handler(site) {
                if (site && !this.editMode) this.applySiteToForm(site);
            },
            deep: true,
        },
        elecEnabledBool(on) {
            if (!on) this.kepcoEnabledBool = false;
        },
    },
    mounted() {
        this.loadAll();
    },
    methods: {
        applySiteToForm(site) {
            this.form = {
                siteName: site.siteName,
                address: site.address || "",
                readingDay: site.readingDay,
                fixedTotalAreaM2: parseFloat(site.fixedTotalAreaM2 || 0),
                householdCount: site.householdCount || null,
                kepcoCustNo: site.kepcoCustNo || "",
                remarks: site.remarks || "",
            };
            this.kepcoEnabledBool = site.kepcoEnabled === "Y";
            this.elecEnabledBool = site.elecEnabled !== "N";
            this.gasEnabledBool = site.gasEnabled === "Y";
            this.waterEnabledBool = site.waterEnabled === "Y";
        },
        numFmt(v) {
            if (v == null || v === "") return "—";
            return Number(v).toLocaleString("ko-KR", { maximumFractionDigits: 2 });
        },
        cancelEdit() {
            this.applySiteToForm(this.site);
            this.editMode = false;
        },
        async saveAndExit() {
            await this.saveSite();
            if (!this.saving) this.editMode = false;
        },
        async loadAll() {
            await Promise.all([
                this.loadHouseholds(),
                this.loadEquipSummary(),
                this.loadScopeTree(),
                this.checkFinal(),
            ]);
        },
        async loadHouseholds() {
            this.loadingHouseholds = true;
            try {
                const { data } = await backEndApi.settlement.getHouseholds(this.site.siteIdx);
                this.households = (Array.isArray(data) ? data : []).map((h) => ({
                    ...h,
                    exclusiveArea: parseFloat(h.exclusiveArea || 0) || null,
                    typeName: h.typeName || "",
                }));
            } catch (err) {
                this.households = [];
            } finally {
                this.loadingHouseholds = false;
            }
        },
        async loadEquipSummary() {
            try {
                const { data } = await backEndApi.settlement.getEquipSummary(this.site.siteIdx);
                this.equipSummary = data;
            } catch (e) {
                this.equipSummary = null;
            }
        },
        async downloadTemplate() {
            try {
                const res = await backEndApi.settlement.exportHouseholdTemplate(this.site.siteIdx);
                const blob = new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `household_template_${this.site.siteIdx}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            }
        },
        async onImportFile(e) {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            try {
                const buf = await file.arrayBuffer();
                const wb = XLSX.read(buf, { type: "array" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet);
                const { data } = await backEndApi.settlement.importHouseholds(this.site.siteIdx, { rows });
                this.alertNoti(`import 완료 — 갱신 ${data.updated}건 / 오류 ${data.errorCount}건`);
                await this.loadHouseholds();
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            }
        },
        async loadScopeTree() {
            this.loadingTree = true;
            try {
                const { data } = await backEndApi.settlement.getSysnodeTree(this.site.scopeSysNodeIdx);
                this.scopeTree = data;
            } catch (e) {
                this.scopeTree = null;
            } finally {
                this.loadingTree = false;
            }
        },
        async checkFinal() {
            try {
                const { data } = await backEndApi.settlement.getSettlementList({
                    siteIdx: this.site.siteIdx,
                    status: "FINAL",
                });
                this.hasFinal = Array.isArray(data) && data.length > 0;
            } catch (e) {
                this.hasFinal = false;
            }
        },
        async saveSite() {
            this.saving = true;
            try {
                const payload = {
                    siteName: this.form.siteName,
                    address: this.form.address || null,
                    readingDay: this.form.readingDay,
                    fixedTotalAreaM2: this.form.fixedTotalAreaM2,
                    householdCount: this.form.householdCount,
                    remarks: this.form.remarks || null,
                    elecEnabled: this.elecEnabledBool ? "Y" : "N",
                    kepcoEnabled: this.elecEnabledBool && this.kepcoEnabledBool ? "Y" : "N",
                    kepcoCustNo: this.elecEnabledBool && this.kepcoEnabledBool ? this.form.kepcoCustNo || null : null,
                    gasEnabled: this.gasEnabledBool ? "Y" : "N",
                    waterEnabled: this.waterEnabledBool ? "Y" : "N",
                };
                const { data } = await backEndApi.settlement.updateSite(this.site.siteIdx, payload);
                this.alertNoti("단지정보 저장 완료");
                this.applySiteToForm(data);
                this.$emit("site-updated", data);
                this.editMode = false;
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.saving = false;
            }
        },
        async saveHouseholds() {
            this.savingHouseholds = true;
            try {
                const items = this.households.map((h) => ({
                    householdIdx: h.householdIdx,
                    exclusiveArea: h.exclusiveArea || 0,
                    typeName: h.typeName || null,
                    isOccupied: h.isOccupied,
                }));
                const { data } = await backEndApi.settlement.bulkUpdateHouseholds({ items });
                this.alertNoti(
                    `세대 ${data.updated}건 저장 완료` + (data.errorCount ? ` / 오류 ${data.errorCount}건` : ""),
                );
                await this.loadHouseholds();
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.savingHouseholds = false;
            }
        },
        async resync() {
            this.syncing = true;
            this.syncResult = null;
            try {
                const { data } = await backEndApi.settlement.syncHouseholds(this.site.siteIdx);
                this.syncResult = data;
                await this.loadHouseholds();
                const { data: site } = await backEndApi.settlement.getSite();
                if (site) {
                    this.applySiteToForm(site);
                    this.$emit("site-updated", site);
                } else if (data.commonSysNodeIdx) {
                    this.$emit("site-updated", { ...this.site, commonSysNodeIdx: data.commonSysNodeIdx });
                }
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.syncing = false;
            }
        },
        async runValidate() {
            this.validating = true;
            this.validateResult = null;
            try {
                const { data } = await backEndApi.settlement.validateSite(this.site.siteIdx);
                this.validateResult = data;
            } catch (err) {
                this.alertWarning(parseApiError(err).message);
            } finally {
                this.validating = false;
            }
        },
    },
};
</script>
